<script setup lang="ts">
import { computed, ref } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import { tutorModule } from '@/store/tutor/module'
import { userModule } from '@/store/user/module'
import { screenModule } from '@/store/screen/module'

const { Icon, Tooltip } = UIKit

const emit = defineEmits<{
    ratingSelected: [rating: -1 | 0 | 1 ]
}>()

const brandColors = BrandColors
const showThumbsUpTooltip = ref(false)
const showThumbsDownTooltip = ref(false)

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const tutorSessionRating = computed(() => {
    return tutorModule.state.rating
})

const ratingClicked = (rating: -1 | 1, event: Event) => {
    // if the user activates the rating that is already selected emit 0
    // this allows the user to unselect a rating
    if (rating === tutorSessionRating.value) {
        emit('ratingSelected', 0)
    } else {
        emit('ratingSelected', rating)
    }
    // Drop focus so :focus-visible outline does not stay after Enter/Space
    (event.currentTarget as HTMLElement).blur()
}

</script>

<template>
    <div
        class="tutor-session-rating"
        role="group"
    >
        <span class="tutor-session-rating__sr-only">Rate this session.</span>
        <div
            class="tutor-session-rating__thumbs-up"
            :class="{ 'tutor-session-rating__thumbs-up--selected': tutorSessionRating === 1 }"
            v-dark
            role="button"
            tabindex="0"
            aria-label="Thumbs up - helped me learn"
            :aria-pressed="tutorSessionRating === 1"
            @click="ratingClicked(1, $event)"
            @keydown.enter.prevent="ratingClicked(1, $event)"
            @keydown.space.prevent="ratingClicked(1, $event)"
            @focus="showThumbsUpTooltip = true"
            @blur="showThumbsUpTooltip = false"
            @mouseover="showThumbsUpTooltip = true"
            @mouseleave="showThumbsUpTooltip = false"
        >
            <Tooltip
                v-if="showThumbsUpTooltip && breakpoint !== 'black-bear'"
                class="tutor-session-rating__thumbs-up-tooltip"
                :is-dark-mode="isDarkMode"
                :styles="{
                    'backgroundColor': brandColors.fog,
                    'color': brandColors.brandBlack,
                }"
            >
                Helped me learn
            </Tooltip>
            <Icon
                class="tutor-session-rating__thumbs-up-icon"
                :type="tutorSessionRating === 1 ? 'thumbsUpOutlineFilled' : 'thumbsUpOutline'"
            />
        </div>
        <div
            class="tutor-session-rating__thumbs-down"
            :class="{ 'tutor-session-rating__thumbs-down--selected': tutorSessionRating === -1 }"
            v-dark
            role="button"
            tabindex="0"
            aria-label="Thumbs down - didn't help me learn"
            :aria-pressed="tutorSessionRating === -1"
            @click="ratingClicked(-1, $event)"
            @keydown.enter.prevent="ratingClicked(-1, $event)"
            @keydown.space.prevent="ratingClicked(-1, $event)"
            @focus="showThumbsDownTooltip = true"
            @blur="showThumbsDownTooltip = false"
            @mouseover="showThumbsDownTooltip = true"
            @mouseleave="showThumbsDownTooltip = false"
        >
            <Tooltip
                v-if="showThumbsDownTooltip && breakpoint !== 'black-bear'"
                class="tutor-session-rating__thumbs-down-tooltip"
                :is-dark-mode="isDarkMode"
                :styles="{
                    'backgroundColor': brandColors.fog,
                    'color': brandColors.brandBlack,
                }"
            >
                Didn't help me learn
            </Tooltip>
            <Icon
                class="tutor-session-rating__thumbs-down-icon"
                :type="tutorSessionRating === -1? 'thumbsUpOutlineFilled' : 'thumbsUpOutline'"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.tutor-session-rating {
    display: flex;

    &__sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        white-space: nowrap;
        border: 0;
    }

    &__thumbs-up,
    &__thumbs-down {
        position: relative;
        display: flex;
        justify-content: center;
        width: 19px;
        height: 19px;
        padding: 6px;
        cursor: pointer;
        border-radius: 6px;

        &:focus {
            outline: none;
        }

        &:focus-visible {
            outline-offset: 0;
            background-color: $brand-black;
        }

        &:hover {
            background-color: $brand-black;
        }

        &:not(&--selected) {
            color: $cloudy;

            &:focus-visible {
                color: $banana-bread;
            }

            &:hover {
                color: $banana-bread;
            }
        }

        &--dark {
            &:hover,
            &:focus {
                background-color: $moonlit-ocean;
            }
        }
    }

    &__thumbs-up-icon,
    &__thumbs-down-icon {
        width: 19px;
        height: 19px;
    }

    &__thumbs-down-icon {
        transform: rotate(180deg);
    }

    &__thumbs-up-tooltip {
        position: absolute;
        bottom: calc(100% - 32px);
        left: calc(50% - 1px);
    }

    &__thumbs-down-tooltip {
        position: absolute;
        bottom: calc(100% - 50px);
        left: 50%;
    }
}
</style>
